import Swiper from 'swiper';


class ReviewsSlider extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.sectionId = this.getAttribute('data-section-id');
    // Initialize with section ID
    
    this.elements = {
      slides: this.querySelectorAll('.swiper-slide'),
      pagination: this.querySelector('.swiper-pagination'),
      nextButton: this.querySelector('.swiper-button-next'),
      prevButton: this.querySelector('.swiper-button-prev'),
    };
    
    // Elements found and ready for initialization
    
    this.swiperEl = this.querySelector('.swiper');
    // Swiper element reference
    
    this.swiperParams = {
      slidesPerView: 1,
      spaceBetween: 20,
      watchOverflow: true,
      loop: false,
      autoHeight: true,
      a11y: {
        enabled: true,
        prevSlideMessage: 'Previous review',
        nextSlideMessage: 'Next review',
        firstSlideMessage: 'This is the first review',
        lastSlideMessage: 'This is the last review',
        paginationBulletMessage: 'Go to review {{index}}'
      },
      pagination: {
        el: this.elements.pagination,
        clickable: true,
        bulletActiveClass: 'swiper-pagination-bullet-active',
        bulletClass: 'swiper-pagination-bullet',
        renderBullet: function (index, className) {
          return `<span class="${className}" role="button" aria-label="Go to review ${index + 1}"></span>`;
        }
      },
      navigation: {
        nextEl: this.elements.nextButton,
        prevEl: this.elements.prevButton,
        disabledClass: 'swiper-button-disabled',
      },
      on: {
        init: () => {
          this.handleSlideChange(0);
          this.dispatchEvent(new CustomEvent('reviews-slider:init'));
        },
        slideChange: (swiper) => {
          this.handleSlideChange(swiper.activeIndex);
        }
      }
    };
  }

  connectedCallback() {
    // Connected to DOM
    if (this.swiper) return;
    
    // Wait for DOM to be ready before initializing
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        // Initialize slider when DOM is loaded
        this.initSwiperSlider();
      });
    } else {
      // DOM already loaded, initialize immediately
      this.initSwiperSlider();
    }
    
    // Add keyboard navigation support
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        this.swiper?.slidePrev();
      } else if (e.key === 'ArrowRight') {
        this.swiper?.slideNext();
      }
    });
  }

  disconnectedCallback() {
    // Clean up when disconnected
    this.destroySlider();
  }

  initSwiperSlider() {
    // Initialize Swiper slider
    
    if (!this.swiperEl) {
      // Swiper element not found
      return;
    }
    
    if (this.swiper) {
      // Swiper already initialized
      return;
    }
    
    // Check if we should disable on desktop
    const disableOnDesktop = this.hasAttribute('data-disable-on-desktop');
    const isDesktop = window.innerWidth >= 1024;
    
    // Check desktop settings
    
    if (disableOnDesktop && isDesktop) {
      // Skip initialization on desktop
      return;
    }
    
    // Configure Swiper with parameters
    try {
      this.swiper = new Swiper(this.swiperEl, this.swiperParams);
      // Swiper initialized
    } catch (error) {
      // Error initializing Swiper
    }
  }

  handleSlideChange(activeIndex) {
    // Get active slide
    if (!this.swiper || !this.swiper.slides) return;
    
    const activeSlide = this.swiper.slides[activeIndex];
    if (!activeSlide) return;
    
    // Announce to screen readers which slide is active
    const slideTitle = activeSlide.querySelector('.review-author')?.textContent || `Review ${activeIndex + 1}`;
    this.announceToScreenReader(`Now viewing review by ${slideTitle}`);
  }

  announceToScreenReader(message) {
    // Create an aria-live region for accessibility announcements
    let announcement = this.querySelector('.sr-announcement');
    if (!announcement) {
      announcement = document.createElement('div');
      announcement.className = 'sr-announcement';
      announcement.setAttribute('aria-live', 'polite');
      announcement.setAttribute('aria-atomic', 'true');
      announcement.style.position = 'absolute';
      announcement.style.width = '1px';
      announcement.style.height = '1px';
      announcement.style.overflow = 'hidden';
      announcement.style.clip = 'rect(0, 0, 0, 0)';
      this.appendChild(announcement);
    }
    announcement.textContent = message;
  }

  destroySlider() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }
}

// Define the custom element
if (!customElements.get('reviews-slider')) {
  customElements.define('reviews-slider', ReviewsSlider);
}
